import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  Users, Plus, UserCog, Shield, Mail, Hash,
  Pencil, UserX, Check, X, Loader2
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const PERM_LABELS = {
  ver_pacientes: { label: "Ver pacientes", desc: "Acceder a la lista y ficha de residentes" },
  crear_pacientes: { label: "Crear pacientes", desc: "Dar de alta nuevos residentes" },
  editar_pacientes: { label: "Editar pacientes", desc: "Modificar datos de residentes existentes" },
  ver_medicacion: { label: "Ver medicación", desc: "Consultar rondas, prescripciones y stock" },
  registrar_medicacion: { label: "Registrar medicación", desc: "Administrar medicamentos a residentes" },
  cargar_registros: { label: "Cargar registros", desc: "Crear registros diarios y signos vitales" },
  ver_registros: { label: "Ver registros", desc: "Consultar registros e historial" },
  reportar_incidentes: { label: "Reportar incidentes", desc: "Crear reportes de incidentes" },
  ver_incidentes: { label: "Ver incidentes", desc: "Consultar incidentes reportados" },
  gestionar_usuarios: { label: "Gestionar usuarios", desc: "Crear, editar y desactivar usuarios" },
};

const PERM_GROUPS = [
  { title: "Pacientes", perms: ["ver_pacientes", "crear_pacientes", "editar_pacientes"] },
  { title: "Medicación", perms: ["ver_medicacion", "registrar_medicacion"] },
  { title: "Registros", perms: ["cargar_registros", "ver_registros"] },
  { title: "Incidentes", perms: ["reportar_incidentes", "ver_incidentes"] },
  { title: "Sistema", perms: ["gestionar_usuarios"] },
];

const STAFF_ROLE_LABELS = {
  enfermeria: "Enfermería",
  administracion: "Administración",
};

const ROLE_LABELS = {
  tenant_admin: "Director / Admin",
  staff_user: "Personal",
  family_user: "Familiar",
};

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [presets, setPresets] = useState({});
  const [allPerms, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const hasAccess = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("gestionar_usuarios");

  const fetchData = useCallback(async () => {
    if (!hasAccess) { setLoading(false); return; }
    try {
      const [usersRes, permsRes] = await Promise.all([
        api.get("/users"),
        api.get("/permissions/all"),
      ]);
      setUsers(usersRes.data);
      setPresets(permsRes.data.presets);
      setAllPerms(permsRes.data.permissions);
    } catch { /* ignore */ }
    setLoading(false);
  }, [hasAccess]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-no-access">
        <p className="text-slate-400">No tenés permisos para gestionar usuarios.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Usuarios</h1>
          <p className="text-sm text-slate-400 mt-0.5">{users.length} usuarios en el geriátrico</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando usuarios...</p>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <UserCard
              key={u.id}
              userData={u}
              isCurrentUser={u.id === user?.id}
              onEdit={() => setEditUser(u)}
              onDeactivate={async () => {
                try {
                  await api.delete(`/users/${u.id}`);
                  toast.success("Usuario desactivado");
                  fetchData();
                } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
              }}
            />
          ))}
        </div>
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        presets={presets}
        allPerms={allPerms}
        onCreated={() => { fetchData(); setCreateOpen(false); }}
      />

      <EditUserDialog
        user={editUser}
        onOpenChange={(open) => { if (!open) setEditUser(null); }}
        allPerms={allPerms}
        onSaved={() => { fetchData(); setEditUser(null); }}
      />
    </div>
  );
}

function UserCard({ userData, isCurrentUser, onEdit, onDeactivate }) {
  const u = userData;
  const isAdmin = u.role === "tenant_admin";
  const isInactive = u.active === false;

  return (
    <Card className={`rounded-2xl border-slate-100 shadow-sm transition-all duration-200 ${isInactive ? "opacity-50" : ""}`} data-testid={`user-card-${u.id}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-base flex-shrink-0 ${
            isAdmin ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"
          }`}>
            {u.name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 text-sm">{u.name}</h3>
              <Badge className={`rounded-full text-[10px] font-semibold border-0 ${
                isAdmin ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"
              }`}>
                {isAdmin ? "Director" : STAFF_ROLE_LABELS[u.staff_role] || ROLE_LABELS[u.role] || u.role}
              </Badge>
              {isInactive && <Badge className="rounded-full text-[10px] font-semibold bg-rose-50 text-rose-600 border-0">Inactivo</Badge>}
              {isCurrentUser && <Badge className="rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border-0">Vos</Badge>}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span>
              {u.matricula && <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {u.matricula}</span>}
            </div>
            {u.role === "staff_user" && u.permissions && (
              <div className="flex flex-wrap gap-1 mt-2">
                {u.permissions.slice(0, 4).map(p => (
                  <span key={p} className="text-[10px] bg-slate-50 text-slate-500 rounded-full px-2 py-0.5">
                    {PERM_LABELS[p]?.label || p}
                  </span>
                ))}
                {u.permissions.length > 4 && (
                  <span className="text-[10px] text-slate-400">+{u.permissions.length - 4} más</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isAdmin && !isCurrentUser && (
              <>
                <Button variant="ghost" size="icon" onClick={onEdit} className="rounded-full hover:bg-violet-50 hover:text-violet-600 w-9 h-9" data-testid={`edit-user-${u.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                {!isInactive && (
                  <Button variant="ghost" size="icon" onClick={onDeactivate} className="rounded-full hover:bg-rose-50 hover:text-rose-500 w-9 h-9" data-testid={`deactivate-user-${u.id}`}>
                    <UserX className="w-4 h-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PermissionsEditor({ permissions, onChange, allPerms }) {
  const toggle = (perm) => {
    if (permissions.includes(perm)) {
      onChange(permissions.filter(p => p !== perm));
    } else {
      onChange([...permissions, perm]);
    }
  };

  return (
    <div className="space-y-5">
      {PERM_GROUPS.map(group => (
        <div key={group.title}>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-2">{group.title}</p>
          <div className="space-y-1">
            {group.perms.map(perm => (
              <div
                key={perm}
                className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => toggle(perm)}
                data-testid={`perm-toggle-${perm}`}
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{PERM_LABELS[perm]?.label}</p>
                  <p className="text-xs text-slate-400">{PERM_LABELS[perm]?.desc}</p>
                </div>
                <Switch
                  checked={permissions.includes(perm)}
                  onCheckedChange={() => toggle(perm)}
                  className="data-[state=checked]:bg-violet-600"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, presets, allPerms, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", staff_role: "enfermeria", matricula: "" });
  const [permissions, setPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", password: "", staff_role: "enfermeria", matricula: "" });
      setPermissions(presets["enfermeria"] || []);
    }
  }, [open, presets]);

  const handleRoleChange = (role) => {
    setForm(prev => ({ ...prev, staff_role: role }));
    setPermissions(presets[role] || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        ...form,
        role: "staff_user",
        permissions,
      });
      toast.success(`Usuario "${form.name}" creado`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear usuario");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Nuevo Usuario</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Crear un nuevo miembro del equipo</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2">
              <Label className="font-medium text-sm text-slate-700">Nombre Completo</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200 focus:border-violet-500" required data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200 focus:border-violet-500" required data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Contraseña</Label>
              <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200 focus:border-violet-500" required data-testid="new-user-password" />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Rol</Label>
              <Select value={form.staff_role} onValueChange={handleRoleChange}>
                <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enfermeria">Enfermería</SelectItem>
                  <SelectItem value="administracion">Administración</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Matrícula</Label>
              <Input value={form.matricula} onChange={e => setForm({...form, matricula: e.target.value})} placeholder="Opcional" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="new-user-matricula" />
            </div>
          </div>

          <Separator className="bg-slate-100" />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Permisos</h3>
              <Badge className="rounded-full bg-violet-50 text-violet-700 border-0 text-[10px] font-semibold">{permissions.length} activos</Badge>
            </div>
            <PermissionsEditor permissions={permissions} onChange={setPermissions} allPerms={allPerms} />
          </div>

          <Button type="submit" disabled={submitting} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-new-user">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</> : <><Check className="w-4 h-4 mr-2" /> Crear Usuario</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user: editUser, onOpenChange, allPerms, onSaved }) {
  const [form, setForm] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editUser) {
      setForm({ name: editUser.name, email: editUser.email, staff_role: editUser.staff_role || "enfermeria", matricula: editUser.matricula || "" });
      setPermissions(editUser.permissions || []);
    }
  }, [editUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setSubmitting(true);
    try {
      await api.put(`/users/${editUser.id}`, { ...form, permissions });
      toast.success("Usuario actualizado");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={!!editUser} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Editar Usuario</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Modificar datos y permisos del usuario</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2">
              <Label className="font-medium text-sm text-slate-700">Nombre</Label>
              <Input value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Email</Label>
              <Input type="email" value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-user-email" />
            </div>
            <div className="space-y-2">
              <Label className="font-medium text-sm text-slate-700">Rol</Label>
              <Select value={form.staff_role || "enfermeria"} onValueChange={v => setForm({...form, staff_role: v})}>
                <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enfermeria">Enfermería</SelectItem>
                  <SelectItem value="administracion">Administración</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="font-medium text-sm text-slate-700">Matrícula</Label>
              <Input value={form.matricula || ""} onChange={e => setForm({...form, matricula: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-user-matricula" />
            </div>
          </div>

          <Separator className="bg-slate-100" />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Permisos</h3>
              <Badge className="rounded-full bg-violet-50 text-violet-700 border-0 text-[10px] font-semibold">{permissions.length} activos</Badge>
            </div>
            <PermissionsEditor permissions={permissions} onChange={setPermissions} allPerms={allPerms} />
          </div>

          <Button type="submit" disabled={submitting} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-edit-user">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : <><Check className="w-4 h-4 mr-2" /> Guardar Cambios</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
